<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Members"
      subtitle="Manage all resident members of the society"
      add-label="Add"
      :show-view="true"
      :show-edit="true"
      :show-delete="true"
      @add="$router.push('/societyadmin/member/add')"
      @view="
        $router.push(
          '/societyadmin/member/view/' +
            $event.id +
            '?unit_mapping_id=' +
            $event.unit_mapping_id,
        )
      "
      @edit="$router.push('/societyadmin/member/edit/' + $event.id)"
      @delete="confirmDelete($event.id)"
      :columns="columns"
      :items="members"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Header extra: block filter + export -->
      <template #header-extra>
        <div class="header-controls">
          <FilterDropdown
            v-if="useBlocks"
            v-model="selected_flat"
            :options="flats"
            item-text="name"
            item-value="id"
            placeholder="Filter by Block"
            @change="refreshTable"
          />

          <button
            class="pt-export-btn"
            :style="{ background: brandColor }"
            @click="exportDialog = true"
            title="Export to Excel"
          >
            <v-icon size="16" color="white">mdi-download-outline</v-icon>
          </button>
        </div>
      </template>

      <!-- Avatar column -->
      <template #col-profile_url="{ item }">
        <div class="mem-avatar-wrap">
          <img
            v-if="item.profile_url && item.profile_url !== 'null'"
            :src="
              API_BASE_URL + '/uploads/members/profile_pics/' + item.profile_url
            "
            :alt="item.name"
            class="mem-avatar-img"
          />
          <div
            v-else
            class="mem-avatar-fallback"
            :style="{ background: primaryColor + '20', color: primaryColor }"
          >
            {{
              item.name && item.name !== "null"
                ? item.name.charAt(0).toUpperCase()
                : "U"
            }}
          </div>
          <span class="mem-name">{{
            item.name && item.name !== "null" ? item.name : "—"
          }}</span>
        </div>
      </template>

      <!-- Unit number column -->
      <template #col-house_number="{ item }">
        <div class="house-number-badge">
          <v-icon size="14" color="#94A3B8">mdi-home-outline</v-icon>
          <span v-if="item.flat_name" class="flat-prefix"
            >{{ item.flat_name }}-</span
          >
          <span class="house-num">{{ item.house_number }}</span>
        </div>
      </template>

      <!-- Last active column -->
      <template #col-last_active_time="{ item }">
        <span class="date-text">{{
          item.last_active_time | formatDateTime
        }}</span>
      </template>
      <template #col-role="{ item }">
        <v-chip
          small
          label
          :color="item.role === 'primary' ? 'primary' : 'orange'"
          dark
          class="font-weight-bold text-uppercase"
          style="height: 20px; font-size: 10px"
        >
          {{ item.role }}
        </v-chip>
      </template>

      <!-- Mobile Number -->
      <template #col-mobile_no="{ item }">
        <div
          style="
            display: flex;
            align-items: center;
            font-size: 13px;
            font-weight: 600;
            color: var(--tx2);
          "
        >
          <v-icon size="14" color="#94A3B8" class="mr-1"
            >mdi-phone-outline</v-icon
          >
          <span class="phone-text"
            >{{
              item.country_code
                ? (item.country_code.toString().startsWith("+")
                    ? item.country_code
                    : "+" + item.country_code) + " "
                : ""
            }}{{ item.mobile_no || "—" }}</span
          >
        </div>
      </template>
    </PremiumTable>

    <!-- ── Confirm Delete Modal ─────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Member"
      message="Are you sure you want to delete the member and all their associated data?"
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteHouse"
    />

    <!-- ── Export Confirmation ──────────────── -->
    <ConfirmModal
      v-model="exportDialog"
      type="primary"
      :btnColor="brandColor"
      title="Export to Excel"
      message="Are you sure you want to export the member list to an Excel file?"
      confirm-text="Yes, Export"
      @confirm="
        exportToExcel();
        exportDialog = false;
      "
    />
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import moment from "moment";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "HouseList",

  mixins: [filterMixin],

  components: { PremiumTable, ConfirmModal, FilterDropdown },

  data() {
    return {
      members: [],
      totalRecords: 0,
      loading: false,
      deleteLoading: false,
      deleteDialog: false,
      delete_id: null,
      flats: [],
      selected_flat: "",
      search: "",
      page: 1,
      itemsPerPage: 10,
      user: {},
      exportDialog: false,
    };
  },

  computed: {
    ...mapGetters("society", ["useBlocks", "unitLabel", "unitType"]),
    ...mapState("theme", ["primaryColor", "buttonColor"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    columns() {
      return [
        { key: "profile_url", label: "Name", width: "250px" },
        { key: "house_number", label: this.unitLabel || "Unit", width: "120px" },
        // { key: "role", label: "Role" },
        { key: "mobile_no", label: "Contact No", hideOnMobile: true },
        { key: "last_active_time", label: "Last Active", hideOnMobile: true, hideOnTablet: true },
      ];
    },
  },

  watch: {
    page() {
      this.getMembers();
    },
    itemsPerPage() {
      this.getMembers();
    },
    search() {
      this.page = 1;
      this.getMembers();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    refreshTable() {
      this.page = 1;
      this.getMembers();
    },

    onSearch(val) {
      this.search = val;
    },

    getMembers() {
      this.loading = true;
      let flat_id = this.selected_flat;
      let unit_type = "";

      if (flat_id === "all_shops") {
        flat_id = "";
        unit_type = 2;
      }

      this.$axios
        .get("societyadmin/members", {
          params: {
            society_id: this.user.id,
            flat_id: flat_id || "",
            unit_type: unit_type,
            page: this.page,
            itemsPerPage: this.itemsPerPage,
            sortBy: "created_at",
            sortDesc: true,
            search: this.search,
          },
        })
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.members = data.members.data;
            this.totalRecords = data.members.total;
            if (data.members.page > data.members.lastPage) this.page = 1;
          } else {
            this.showError(data.message);
            if (data.type === "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteHouse() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/members/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getMembers();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    getFlats() {
      this.$axios.get("societyadmin/flats").then((response) => {
        const data = response.data;
        if (data.status) {
          let flats = [...data.flats];
          if (this.unitType === 3 && this.useBlocks) {
            flats.unshift({ id: "all_shops", name: "All Shops" });
          }
          this.flats = flats;
        }
      });
    },

    exportToExcel() {
      this.loading = true;
      this.$axios
        .get("societyadmin/export/members", {
          params: {
            society_id: this.user.id,
            flat_id: this.selected_flat,
            search: this.search,
          },
          responseType: "blob",
        })
        .then((response) => {
          this.loading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Members_${moment().format("YYYY-MM-DD")}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(() => {
          this.loading = false;
          this.showError("Failed to export data");
        });
    },

    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY · hh:mm A") : "—";
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    this.getFlats();

    // Initialize persistent filters for this module
    this.initFilters("member");

    this.getMembers();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

@media (max-width: 600px) {
  .header-controls {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 8px;
  }

  .header-controls >>> .v-input,
  .header-controls >>> .filter-dropdown-wrap {
    width: 100% !important;
  }
}

/* ── Header extra controls ── */
.pt-icon-btn {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--surface2, #f8fafc);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}
.pt-icon-btn:hover {
  background: var(--surface3, #f1f5f9);
}

/* ── Avatar ── */
.mem-avatar-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mem-avatar-img {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  border-radius: 50%;
  object-fit: contain;
  flex-shrink: 0;
  border: 2px solid var(--border, #e2e8f0);
}
.mem-avatar-fallback {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.mem-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx, #111827);
}

/* ── Other cell styles ── */
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  /* font-weight: 700; */
  color: var(--tx, #111827);
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}

.unit-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 11px;
  font-weight: 600;
  color: var(--tx2, #374151);
}
.phone-text,
.date-text {
  font-size: 12.5px;
  color: var(--tx2, #374151);
}
</style>
